<script setup>
import {onMounted, ref} from 'vue';
import {useDataTable} from '@/utils/datatableHelper';
import {useToast} from "vue-toastification";
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import IdentificationModal from '@/components/modals/IdentificationModal.vue';
import IdentificationService from '@/services/IdentificationService';
import {authData} from '@/services/AuthService';

const props = defineProps({
  clientId: Number
});

const toast = useToast();

import {useI18n} from 'vue-i18n'

const {t} = useI18n()

/**
 * Mapování hodnot BE IdentificationMethodEnum na překladové klíče.
 * BE drží duplicitní (historické) hodnoty IDCARD/ID_CARD a DRIVING_LICENSE/DRIVER_LICENSE,
 * proto mají stejný popisek. Při rozšíření enumu na BE je potřeba doplnit i sem.
 */
const methodLabelKeys = {
  IDCARD: 'identificationModal.method_personal_id',
  ID_CARD: 'identificationModal.method_personal_id',
  DRIVING_LICENSE: 'identificationModal.method_driver_licence',
  DRIVER_LICENSE: 'identificationModal.method_driver_licence',
  PASSPORT: 'identificationModal.method_passport',
  BIRTH_CERTIFICATE: 'identificationModal.method_birth_certificate',
  RESIDENCE_PERMIT: 'identificationModal.method_residence_permit',
};

// Bank ID je název služby (nepřekládá se); neznámou hodnotu zobrazíme surově, ať sloupec nezůstane prázdný.
const methodLabel = (method) => {
  if (method === 'BANKID') {
    return 'Bank ID';
  }
  return methodLabelKeys[method] ? t(methodLabelKeys[method]) : method;
};


const isLoading = ref(false);
const datatable = useDataTable(fetchRecords, 'created_at', 10, false);
const rows = ref([]);
const columns = ref([
  {label: t('identificationsIndex.date'), field: 'created_at', sortable: false, filterOptions: {enabled: false}, formatFn: (value) => formatDate(value, 'd.m.Y H:i')},
  {label: t('identificationsIndex.status'), field: 'state', sortable: false},
  {label: t('identificationsIndex.method'), field: 'method', sortable: false},
  {label: t('identificationsIndex.action'), field: 'action', sortable: false},
]);

function fetchRecords(body) {
  body = {...body,...{filter: {client_id: props.clientId}}};
  IdentificationService.fetchRecords(props.clientId, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    rows.value = response.data;
  });
}

function viewIdentification(identification) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(IdentificationModal, {identification, disabled: identification.state !== 'CONCEPT'});
  reveal();
  onConfirm((data) => {
    data.client_id = props.clientId;
    data.creator_id = authData.loggedUser.id;
    IdentificationService.saveIdentification(props.clientId, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('identificationSaved');
    });
  });
  onCancel(() => {
  });
}

function deleteIdentification(identification) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('identificationsIndex.delete_identification'),
    message: t('identificationsIndex.confirm_delete_entry'),
  });
  onConfirm(() => {
    IdentificationService.deleteRecord(props.clientId, identification.id).then(() => {
      showSuccessMessage(t('identificationsIndex.identification_is_deleted'));
      datatable.fetchWithValues();
    });
  });
  reveal();
}

onMounted(() => {
  emitter.on('identificationSaved', () => {
    datatable.fetchWithValues();
  });
})
</script>

<template>
  <div class="page-section">
    <div class="page-section__content">
      <div class="mt-3">
        <vue-good-table
            :columns="columns"
            :rows="rows"
            mode="remote"
            @page-change="datatable.onPageChange"
            @sort-change="datatable.onSortChange"
            @column-filter="datatable.onColumnFilter"
            @per-page-change="datatable.onPerPageChange"
            :totalRows="datatable.totalRecords.value"
            :pagination-options="{
                  enabled: true,
                  rowsPerPageLabel: t('identificationsIndex.entriesPerPage'),
                  nextLabel: t('identificationsIndex.next'),
                  prevLabel: t('identificationsIndex.previous'),
                  ofLabel: 'z',
                }">
          <template #table-row="props">
            <span v-if="props.column.field === 'state'">
              <span v-if="props.row.state === 'DENIED'"> {{ t('identificationsIndex.denied') }} </span>
              <span v-if="props.row.state === 'APPROVED'"> {{ t('identificationsIndex.approved') }} </span>
              <span v-if="props.row.state === 'WAITING'"> {{ t('identificationsIndex.waiting') }} </span>
              <span v-if="props.row.state === 'TO_REVIEW'"> {{ t('identificationsIndex.to_review') }} </span>
            <span v-if="props.row.state === 'CANCELED'"> {{ t('identificationsIndex.canceled') }} </span>
            <span v-if="props.row.state === 'ACCEPTED'"> {{ t('identificationsIndex.accepted') }} </span>
            <span v-if="props.row.state === 'CONCEPT'"> {{ t('identificationsIndex.concept') }} </span>

            </span>
            <span v-if="props.column.field === 'method'">
              <span> {{ methodLabel(props.row.method) }} </span>
            </span>
            <span v-if="props.column.field === 'action'">
              <span style="font-weight: bold; float: right">
                <button v-if="props.row.method !== 'BANKID' || props.row.state === 'CONCEPT'" type="button" class="btn btn-link"><i @click="viewIdentification(props.row)" class="mdi mdi-eye"/></button>
                <button type="button" class="btn btn-error" @click="deleteIdentification(props.row)"><i class="mdi mdi-trash-can-outline"/></button>
              </span>
            </span>
          </template>
          <template v-slot:emptystate v-if="!isLoading">
            <div class="text-center">
              {{ t('identificationsIndex.no_records_found') }}
            </div>
          </template>
          <template v-slot:emptystate v-if="isLoading">
            <div class="text-center">
              {{ t('identificationsIndex.loading') }}
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>
